import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import {
    BUILD_NOTES,
    BUILD_TIME,
    VERSION_NUMBER,
} from '../services/version.service';
import { UserInfo, UserService } from '../services/user.service';
import { ConfirmationDialogService } from '@polk_county_public_schools/ngx-pcps-dialogs/confirmation';
import { AuthService } from '../services/auth.service';
import { AppService } from '../services/app.service';
import { SettingsService } from '../services/settings.service';
import { MessageBoxDialogService } from '@polk_county_public_schools/ngx-pcps-dialogs/messagebox';
import { Observable } from 'rxjs';
import { UnsavedChangesService } from '../routes/routes.guard';
import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { UserInfoComponent } from './utilities/user-info/user-info.component';
import { NavMenuComponent } from './utilities/nav-menu/nav-menu.component';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { ScreenSizeService } from '@polk_county_public_schools/ngx-pcps-dialogs/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EnvironmentService } from '../services/environment.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [
        RouterModule,
        MatIconModule,
        UserInfoComponent,
        NavMenuComponent,
        MatToolbarModule,
        MatButtonModule,
        MatTooltipModule
    ],
    providers: [
        DatePipe,
        UnsavedChangesService
    ]
})
export class AppComponent implements OnInit {
    protected readonly destroyRef = inject(DestroyRef);
    protected readonly appSvc = inject(AppService);
    protected readonly authSvc = inject(AuthService);
    protected readonly confirmDlgSvc = inject(ConfirmationDialogService);
    protected readonly datePipe = inject(DatePipe);
    protected readonly messageDlgSvc = inject(MessageBoxDialogService);
    protected readonly screenSizeSvc = inject(ScreenSizeService);
    protected readonly settingsSvc = inject(SettingsService);
    protected readonly unsavedChangesSvc = inject(UnsavedChangesService);
    protected readonly userSvc = inject(UserService);
    protected readonly envSvc = inject(EnvironmentService);
    BuildNotes: string;
    BuildTime: string;
    VersionLoaded: string;
    ViewReady: boolean;
    User: UserInfo;
    smallScreen: boolean;
    scholarshipHelpEmail: string;
    techHelpEmail: string;
    isDevMode: boolean;

    ngOnInit(): void {
        this.BuildNotes = BUILD_NOTES;
        this.VersionLoaded = VERSION_NUMBER;
        this.ViewReady = false;

        this.envSvc.setEnvironment();
        this.isDevMode = this.envSvc.isDevMode;

        this.userSvc.UserSubject.pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe((user) => {
            this.User = user;
        });

        this.appSvc
            .GetSettings()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((res) => {
                this.settingsSvc.UpdateSettings(res);
                this.scholarshipHelpEmail = res.find(
                    (f) =>
                        f.Label.trim() ===
                        'Scholarship help request email address'
                ).Value;
                this.techHelpEmail = res.find(
                    (f) =>
                        f.Label.trim() ===
                        'Technical support request email address'
                ).Value;

                this.ViewReady = true;
            });

        if (this.screenSizeSvc != null) {
            this.screenSizeSvc.ScreenSizeSub
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((size) => {
                    if (size == null) return;
                    this.smallScreen = size.IsSm || size.IsXs;
                });
        }
    }

    help() {
        this.messageDlgSvc.Open({
            TitleText: 'Help',
            MessageText:
                `If you have questions about the application, you can email the following address for answers: ` +
                this.scholarshipHelpEmail +
                `<br><br>If the site has technical issues, please submit a ticket to IncidentIQ. ` +
                `<br>Please include a description of what you were trying to do and what happened. ` +
                `A screenshot of the whole window, including any error message, would also be helpful.` +
                `<br><br>Version Number: ` + VERSION_NUMBER +
                `<br>Build Date: ` + BUILD_TIME
        });
    }

    logout() {
        // If we don't have a change tracker in place, we will defer to the standard confirmation for logouts
        if (this.unsavedChangesSvc.HasTracker() && this.unsavedChangesSvc.HasChanges()) {
            let confObs: Observable<boolean> | null = this.unsavedChangesSvc.CheckTracker?.();

            confObs.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res) => {
                if (res === true) {
                    // User is opting to log out. Take down the active change tracker.
                    this.unsavedChangesSvc.ClearTracking();

                    // Proceed.
                    this.userSvc.logout(null);
                }
            });
        } else {
            this.userSvc.logout(true);
        }


    }
}
